<template>
  <div class="tw-w-full" :class="outerContainerClass">
    <table ref="table" class="sf-full-width tw-font-sans" :class="tableClass">
      <thead>
        <slot
          name="columns"
          :headers="headers"
          :hide-header="hideHeader"
          :sort-direction="sortDirection"
          :sort-by="sortBy"
        >
          <SfTableHeaders
            :headers="headers"
            :hidden="hideHeader"
            :sort-direction="sortDirection"
            :sort-by="sortBy.value"
            :variant="variant"
            :non-sortable-headers="nonSortableHeaders"
            @header-click="headerClick"
          >
            <template #header="{ header, headerIndex, headerLabel }">
              <slot
                name="header"
                :header="header"
                :header-index="headerIndex"
                :header-label="headerLabel"
                :sort-by="sortBy"
                :sort-direction="sortDirection"
                :max-header-cell-width="maxHeaderCellWidth"
                :non-sortable-headers="nonSortableHeaders"
              />
            </template>
          </SfTableHeaders>
        </slot>
      </thead>
      <tbody>
        <slot name="rows" :rows="effectiveData">
          <tr
            v-for="(dataRow, index) in effectiveData"
            :key="dataRow?.id || index"
            class="tw-group/row"
            data-testid="sf-table-row"
          >
            <slot
              :id="dataRow?.id || index"
              name="row"
              :row-data="dataRow"
              :cell-classes="styles.body"
            >
              <SfTableRow
                :row-data="dataRow"
                :ignored-columns="ignoredColumns"
                :variant="variant"
              />
            </slot>
          </tr>
        </slot>
      </tbody>
    </table>
  </div>
</template>

<script
  setup
  lang="ts"
  generic="TR extends BaseTableRow, HD extends BaseHeader"
>
import type { BaseTableRow, BaseHeader, TableSortEvent } from './types'

import { computed, onMounted, ref } from 'vue'

import type { TableSortDirections, TableVariants } from './constants'
import { tableSortDirections, tableVariants } from './constants'
import SfTableHeaders from './sf-table-headers.vue'
import SfTableRow from './sf-table-row.vue'
import { useTableStyles } from './use-cell-styles'
import { defaultTableSort } from './utils'

defineOptions({ name: 'SfTable' })

interface Props {
  autoHeaderCellWidth?: boolean
  handlesSorting?: boolean
  headers?: HD[]
  hideHeader?: boolean
  ignoredColumns?: string[]
  initialSortBy?: HD | ''
  initialSortDirection?: TableSortDirections
  layout?: string
  rows?: TR[]
  rounded?: boolean
  nonSortableHeaders?: string[]
  sortingFunction?: (
    rows: TR[],
    sortBy: HD,
    sortDirection: TableSortDirections
  ) => TR[]
  stickyHeader?: boolean
  variant?: TableVariants
}

const {
  autoHeaderCellWidth = false,
  handlesSorting = true,
  headers = [],
  hideHeader = false,
  ignoredColumns = ['id'],
  initialSortBy = '',
  initialSortDirection = tableSortDirections.ASC,
  layout = 'auto',
  rows = [],
  rounded,
  nonSortableHeaders = [],
  sortingFunction = defaultTableSort,
  stickyHeader = false,
  variant = tableVariants.DEFAULT,
} = defineProps<Props>()

const emit =
  defineEmits<{
    'table-sort': [event: TableSortEvent]
  }>()

const table = ref<HTMLTableElement | null>(null)
const sortBy = ref<HD | ''>(initialSortBy)
const sortDirection = ref<TableSortDirections>(initialSortDirection)
const maxHeaderCellWidth = ref<number>(0)

const { styles } = useTableStyles(() => variant)

onMounted(() => {
  if (!autoHeaderCellWidth) {
    return
  }

  const tableElement = table.value

  if (!tableElement) {
    return
  }

  const tableWidth = tableElement.offsetWidth

  maxHeaderCellWidth.value = Math.floor(tableWidth / headers.length)
})

const outerContainerClass = computed(() => {
  return {
    'tw-overflow-hidden tw-rounded-lg tw-border tw-border-solid tw-border-sf-snow':
      rounded,
    'tw-border-sf-snow': rounded && variant === tableVariants.DEFAULT,
    'tw-border-sf-snow-dark': rounded && variant === tableVariants.FLAT,
  }
})

const effectiveData = computed(() => {
  return handlesSorting ? sortedData.value : rows
})

const sortedData = computed(() => {
  return sortingFunction(rows, sortBy.value, sortDirection.value)
})

const tableClass = computed(() => {
  return {
    'columns-auto-width': layout === 'fixed',
    'sf-table-sticky': stickyHeader,
    'sf-table sf-table-border': variant === 'default',
    'tw-border-collapse': variant === 'flat',
    'sf-table__table--rounded tw-border-none': rounded,
  }
})

const headerClick = (value: string) => {
  if (nonSortableHeaders.includes(value)) {
    return
  }

  const currentSortKey =
    typeof sortBy.value === 'string' ? sortBy.value : sortBy.value.id

  if (value === currentSortKey) {
    sortDirection.value =
      sortDirection.value === tableSortDirections.ASC
        ? tableSortDirections.DESC
        : tableSortDirections.ASC
  }

  sortBy.value = value

  if (!handlesSorting) {
    emit('table-sort', {
      sortBy: sortBy.value,
      sortDirection: sortDirection.value,
    })
  }
}
</script>

<style lang="scss" scoped>
.columns-auto-width {
  table-layout: fixed;
  width: 100%;

  th,
  td {
    width: auto;
  }
}

.sf-table {
  &__table {
    &--rounded {
      :deep(thead:first-child th) {
        border-top: none;
      }

      :deep(tr:first-child td) {
        border-top: none;
      }

      :deep(thead th:first-child) {
        border-left: none;
      }

      :deep(tr td:first-child) {
        border-left: none;
      }

      :deep(thead th:last-child) {
        border-right: none;
      }

      :deep(tr td:last-child) {
        border-right: none;
      }

      :deep(tr:last-child td) {
        border-bottom: none;
      }
    }
  }
}
</style>
